/**
 * Ayah audio playback hook.
 *
 * Wraps a single HTML5 `Audio` element. Caller provides the URL and
 * the repeat preference; the hook handles state (playing / loading /
 * error), repeat counting, and cleanup.
 *
 * Designed for the Memorize workflow but generic enough that any
 * future audio surface (Reciter selector, Reader auto-play, etc.) can
 * reuse it without modification.
 *
 * Lifecycle:
 *   - On `audioUrl` change: stop current audio, reset counters,
 *     load the new source. The user must press play to start
 *     playback (no auto-play, per product decision).
 *   - On unmount: pause and release the audio element.
 *   - On `repeatMode === 'loop'`: native `audio.loop = true` is used
 *     for gapless looping; counter logic is bypassed.
 *   - On `repeatMode` change mid-playback: the new mode takes effect
 *     on the NEXT `ended` event (or immediately for the loop flag).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RepeatMode } from '@/types/memorization';

interface UseAyahAudioOptions {
  /** The audio source. `null` clears the player without loading anything. */
  audioUrl: string | null;
  /** Initial repeat preference. Caller can change via `setRepeatMode`. */
  initialRepeatMode?: RepeatMode;
}

interface UseAyahAudioReturn {
  /** True while the audio is actively playing. */
  isPlaying: boolean;
  /** True from `loadstart` until `canplay`. */
  isLoading: boolean;
  /** Non-null when the audio failed to load or play. */
  error: string | null;
  /** Current repeat mode. */
  repeatMode: RepeatMode;
  /** Update the repeat preference. Takes effect on the next play cycle. */
  setRepeatMode: (mode: RepeatMode) => void;
  /** Start (or resume) playback. */
  play: () => void;
  /** Pause without resetting position. */
  pause: () => void;
  /** Pause AND reset to position 0. Also resets the repeat counter. */
  stop: () => void;
  /** Force the audio element to re-fetch the current URL (recover from error). */
  retry: () => void;
  /** Current playback position in seconds. Resets to 0 on src change. */
  currentTime: number;
  /** Total duration in seconds. 0 until metadata loads. */
  duration: number;
  /** Seek to an absolute position in seconds. Clamped to [0, duration]. */
  seek: (seconds: number) => void;
  /** Seek by a relative delta in seconds. */
  seekRelative: (deltaSeconds: number) => void;
}

const REPEAT_COUNTS: Record<RepeatMode, number> = {
  once: 1,
  three: 3,
  five: 5,
  loop: Infinity,
};

export function useAyahAudio({
  audioUrl,
  initialRepeatMode = 'once',
}: UseAyahAudioOptions): UseAyahAudioReturn {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playCountRef = useRef(0);
  const repeatModeRef = useRef<RepeatMode>(initialRepeatMode);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repeatMode, setRepeatModeState] = useState<RepeatMode>(initialRepeatMode);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Keep ref in sync with state so the `ended` handler sees the latest mode.
  useEffect(() => {
    repeatModeRef.current = repeatMode;
  }, [repeatMode]);

  // Create the Audio element once on mount.
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    function onPlay() {
      setIsPlaying(true);
    }
    function onPause() {
      setIsPlaying(false);
    }
    function onLoadStart() {
      setIsLoading(true);
      setError(null);
    }
    function onCanPlay() {
      setIsLoading(false);
    }
    function onError() {
      setIsLoading(false);
      setIsPlaying(false);
      setError('Audio could not be loaded.');
    }
    function onTimeUpdate() {
      if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
    }
    function onLoadedMetadata() {
      if (audioRef.current && Number.isFinite(audioRef.current.duration)) {
        setDuration(audioRef.current.duration);
      }
    }
    function onEnded() {
      // Loop mode is handled by audio.loop = true; this branch never fires for loop.
      const mode = repeatModeRef.current;
      const target = REPEAT_COUNTS[mode];
      playCountRef.current += 1;
      if (playCountRef.current < target) {
        // Repeat: rewind and play again.
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          void audioRef.current.play().catch(() => {
            // Autoplay may be blocked if user navigated away; ignore.
          });
        }
      } else {
        // Done. Reset counter for next manual play.
        playCountRef.current = 0;
        setIsPlaying(false);
      }
    }

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('loadstart', onLoadStart);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('durationchange', onLoadedMetadata);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('loadstart', onLoadStart);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onLoadedMetadata);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  // Update src whenever audioUrl changes. Do not auto-play.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    playCountRef.current = 0;
    setIsPlaying(false);
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    if (audioUrl) {
      audio.src = audioUrl;
      audio.loop = repeatModeRef.current === 'loop';
      audio.load();
    } else {
      audio.removeAttribute('src');
      audio.load();
    }
  }, [audioUrl]);

  // Keep `audio.loop` in sync with repeatMode without restarting playback.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.loop = repeatMode === 'loop';
    // Reset counter when leaving/entering loop mode so the next sequence
    // counts cleanly from zero.
    playCountRef.current = 0;
  }, [repeatMode]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    setError(null);
    void audio.play().catch((err) => {
      setError(err instanceof Error ? err.message : 'Playback failed.');
      setIsPlaying(false);
    });
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    playCountRef.current = 0;
    setIsPlaying(false);
  }, []);

  const retry = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    audio.load();
  }, []);

  const setRepeatMode = useCallback((mode: RepeatMode) => {
    setRepeatModeState(mode);
  }, []);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = Number.isFinite(audio.duration) ? audio.duration : seconds;
    audio.currentTime = Math.max(0, Math.min(max, seconds));
  }, []);

  const seekRelative = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = Number.isFinite(audio.duration) ? audio.duration : audio.currentTime + delta;
    audio.currentTime = Math.max(0, Math.min(max, audio.currentTime + delta));
  }, []);

  return {
    isPlaying,
    isLoading,
    error,
    repeatMode,
    setRepeatMode,
    play,
    pause,
    stop,
    retry,
    currentTime,
    duration,
    seek,
    seekRelative,
  };
}
